import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { livestreamService } from '../../services/livestream';
import { productService } from '../../services/products';
import webSocketService from '../../services/websocket';
import Alert from '../../components/Common/Alert';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ImageUtils } from '../../utils/imageUtils';
import { emergencyCameraCleanup, comprehensiveCameraCleanup } from '../../utils/cameraCleanup';
import './Livestreams.css';

// Custom hooks for better state management
const useMediaDevices = () => {
  const [stream, setStream] = useState(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [devices, setDevices] = useState({ cameras: [], microphones: [] });
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [selectedMicrophone, setSelectedMicrophone] = useState(null);
  const [permissions, setPermissions] = useState({ camera: false, microphone: false });

  const getDevices = useCallback(async () => {
    try {
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        cameras: deviceList.filter(device => device.kind === 'videoinput'),
        microphones: deviceList.filter(device => device.kind === 'audioinput')
      });
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  }, []);

  const requestPermissions = useCallback(async () => {
    try {
      // First, get devices to ensure we have the latest list
      await getDevices();

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });

      setPermissions({ camera: true, microphone: true });
      setStream(mediaStream);

      // Store stream globally for cleanup during navigation
      window.currentMediaStream = mediaStream;

      // Set initial enabled states based on track states
      const videoTrack = mediaStream.getVideoTracks()[0];
      const audioTrack = mediaStream.getAudioTracks()[0];

      if (videoTrack) {
        setIsVideoEnabled(videoTrack.enabled);
        // Set default camera from actual video track
        if (!selectedCamera && videoTrack.getSettings && videoTrack.getSettings().deviceId) {
          setSelectedCamera(videoTrack.getSettings().deviceId);
        }
      }
      if (audioTrack) {
        setIsAudioEnabled(audioTrack.enabled);
        // Set default microphone from actual audio track
        if (!selectedMicrophone && audioTrack.getSettings && audioTrack.getSettings().deviceId) {
          setSelectedMicrophone(audioTrack.getSettings().deviceId);
        }
      }

      // Fallback: Set default devices if not already set
      if (!selectedCamera && devices.cameras.length > 0) {
        setSelectedCamera(devices.cameras[0].deviceId);
      }
      if (!selectedMicrophone && devices.microphones.length > 0) {
        setSelectedMicrophone(devices.microphones[0].deviceId);
      }

      console.log('✅ Media permissions granted and devices initialized');
      return mediaStream;
    } catch (error) {
      console.error('Permission denied:', error);
      setPermissions({ camera: false, microphone: false });
      setIsVideoEnabled(false);
      setIsAudioEnabled(false);
      throw error;
    }
  }, [devices.cameras, devices.microphones, selectedCamera, selectedMicrophone, getDevices]);

  const toggleVideo = useCallback(async () => {
    if (!stream) {
      console.log('📹 No stream available, requesting initial camera access');
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: selectedCamera ?
            { deviceId: { exact: selectedCamera }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } } :
            { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
          audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true
        });

        setStream(newStream);
        window.currentMediaStream = newStream;
        setIsVideoEnabled(true);

        const audioTrack = newStream.getAudioTracks()[0];
        if (audioTrack) {
          setIsAudioEnabled(audioTrack.enabled);
        }

        console.log('📹 Camera enabled with new stream');
        return;
      } catch (error) {
        console.error('Failed to get initial camera access:', error);
        setIsVideoEnabled(false);
        return;
      }
    }

    const videoTrack = stream.getVideoTracks()[0];

    // Check if we're currently in video-enabled state or video-disabled state
    if (videoTrack && videoTrack.enabled) {
      // Turning camera OFF - properly stop all video tracks and recreate stream without video
      console.log('📹 Turning camera OFF - stopping all video tracks');

      // Stop all video tracks to release camera hardware
      stream.getVideoTracks().forEach(track => {
        track.stop();
        stream.removeTrack(track);
        console.log(`Stopped video track: ${track.label}`);
      });

      // Create new stream with only audio to maintain audio functionality
      try {
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack && audioTrack.enabled) {
          // Keep existing audio track
          const audioStream = new MediaStream([audioTrack]);
          setStream(audioStream);
        } else {
          // Create new audio-only stream
          const audioOnlyStream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true
          });
          setStream(audioOnlyStream);
        }
      } catch (error) {
        console.warn('Failed to maintain audio stream:', error);
        // If audio fails, create empty stream
        setStream(new MediaStream());
      }

      setIsVideoEnabled(false);
      console.log('📹 Camera completely stopped and disabled');

    } else {
      // Turning camera ON - create new video stream
      // This handles both cases: disabled video track or no video track at all
      console.log('📹 Turning camera ON - requesting new video access');

      try {
        // Stop current stream completely
        if (stream) {
          stream.getTracks().forEach(track => {
            track.stop();
            console.log(`Stopped ${track.kind} track for camera restart`);
          });
        }

        // Small delay to ensure hardware is properly released
        await new Promise(resolve => setTimeout(resolve, 100));

        // Create completely new stream with video and audio
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: selectedCamera ?
            { deviceId: { exact: selectedCamera }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } } :
            { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
          audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true
        });

        setStream(newStream);
        window.currentMediaStream = newStream;

        // Update states based on new stream
        const newVideoTrack = newStream.getVideoTracks()[0];
        const newAudioTrack = newStream.getAudioTracks()[0];

        if (newVideoTrack) {
          setIsVideoEnabled(newVideoTrack.enabled);
        }
        if (newAudioTrack) {
          setIsAudioEnabled(newAudioTrack.enabled);
        }

        console.log('📹 Camera enabled with completely new stream');
      } catch (error) {
        console.error('Failed to re-enable camera:', error);
        setIsVideoEnabled(false);
        return;
      }
    }
  }, [stream, selectedCamera, selectedMicrophone]);

  const toggleAudio = useCallback(async () => {
    if (!stream) return;

    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setIsAudioEnabled(audioTrack.enabled);
      console.log(`🎤 Microphone ${audioTrack.enabled ? 'enabled' : 'disabled'}`);
    }
  }, [stream]);

  const switchCamera = useCallback(async (deviceId) => {
    try {
      console.log(`📷 Switching camera to device: ${deviceId}`);

      // Completely stop all current tracks to release hardware
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
          console.log(`Stopped ${track.kind} track for camera switch`);
        });
      }

      // Small delay to ensure hardware is properly released
      await new Promise(resolve => setTimeout(resolve, 100));

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        },
        audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true
      });

      setStream(newStream);
      window.currentMediaStream = newStream;
      setSelectedCamera(deviceId);

      // Update enabled states for new stream
      const videoTrack = newStream.getVideoTracks()[0];
      const audioTrack = newStream.getAudioTracks()[0];

      if (videoTrack) {
        setIsVideoEnabled(videoTrack.enabled);
        console.log(`📷 Camera switched successfully to: ${videoTrack.label}`);
      }
      if (audioTrack) {
        setIsAudioEnabled(audioTrack.enabled);
      }

      return newStream;
    } catch (error) {
      console.error('Error switching camera:', error);
      // Try to restore previous stream if switch fails
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true
        });
        setStream(fallbackStream);
      } catch (fallbackError) {
        console.error('Failed to restore camera after switch error:', fallbackError);
      }
      throw error;
    }
  }, [stream, selectedMicrophone]);

  const cleanup = useCallback(() => {
    console.log('🧹 Cleaning up media devices and releasing camera hardware');

    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
        console.log(`Stopped and released ${track.kind} track: ${track.label}`);
      });
      setStream(null);

      // Clear global reference if this is the current stream
      if (window.currentMediaStream === stream) {
        window.currentMediaStream = null;
      }
    }

    // Clear video element references immediately
    const videoElements = document.querySelectorAll('video');
    videoElements.forEach(video => {
      if (video.srcObject) {
        const videoStream = video.srcObject;
        if (videoStream && typeof videoStream.getTracks === 'function') {
          videoStream.getTracks().forEach(track => {
            track.stop();
            console.log(`🧹 Cleanup: Stopped ${track.kind} track from video element: ${track.label}`);
          });
        }
        video.srcObject = null;
        video.pause();
      }
    });

    // Use the comprehensive camera cleanup utility
    emergencyCameraCleanup();

    // Also run the comprehensive cleanup for additional safety
    setTimeout(() => {
      comprehensiveCameraCleanup();
    }, 100);

    setIsVideoEnabled(false);
    setIsAudioEnabled(false);
    setSelectedCamera(null);
    setSelectedMicrophone(null);
    console.log('✅ Media cleanup completed');
  }, [stream]);

  const reinitialize = useCallback(async () => {
    console.log('🔄 Reinitializing media devices');

    // Clean up existing stream
    cleanup();

    // Small delay to ensure cleanup is complete
    await new Promise(resolve => setTimeout(resolve, 200));

    // Get fresh device list
    await getDevices();

    // Request permissions again
    try {
      const newStream = await requestPermissions();
      console.log('✅ Media devices reinitialized successfully');
      return newStream;
    } catch (error) {
      console.error('❌ Failed to reinitialize media devices:', error);
      throw error;
    }
  }, [cleanup, getDevices, requestPermissions]);

  return useMemo(() => ({
    stream,
    isVideoEnabled,
    isAudioEnabled,
    devices,
    selectedCamera,
    selectedMicrophone,
    permissions,
    getDevices,
    requestPermissions,
    toggleVideo,
    toggleAudio,
    switchCamera,
    cleanup,
    reinitialize
  }), [stream, isVideoEnabled, isAudioEnabled, devices, selectedCamera, selectedMicrophone, permissions, getDevices, requestPermissions, toggleVideo, toggleAudio, switchCamera, cleanup, reinitialize]);
};

const useWebSocketConnection = (livestreamId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);

  const connect = useCallback(async () => {
    try {
      setConnectionStatus('connecting');
      const token = localStorage.getItem('authToken');
      await webSocketService.connect(livestreamId, token);
      setIsConnected(true);
      setConnectionStatus('connected');
      setError(null);
    } catch (err) {
      setError(err.message);
      setConnectionStatus('failed');
      setIsConnected(false);
    }
  }, [livestreamId]);

  const disconnect = useCallback(() => {
    webSocketService.disconnect();
    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  return {
    isConnected,
    connectionStatus,
    error,
    connect,
    disconnect
  };
};

const EnhancedLivestreamControlPanel = () => {
  const { livestreamId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Core state
  const [livestream, setLivestream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Product and bidding state
  const [products, setProducts] = useState([]);
  const [currentBidding, setCurrentBidding] = useState(null);
  const [biddingTimer, setBiddingTimer] = useState(0);
  const [bids, setBids] = useState([]);
  const [showBiddingModal, setShowBiddingModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Chat state
  const [viewerCount, setViewerCount] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Analytics state
  const [analytics, setAnalytics] = useState({
    duration: 0,
    interactions: 0,
    revenue: 0,
    engagement: 0,
    total_bids: 0,
    total_viewers: 0
  });



  // Custom hooks
  const mediaDevices = useMediaDevices();
  const websocket = useWebSocketConnection(livestreamId);

  // Refs
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const analyticsRef = useRef(null);
  const dataRefreshRef = useRef(null);
  const creditCheckRef = useRef(null);
  const isMountedRef = useRef(true);

  // Agora state
  const [agoraClient, setAgoraClient] = useState(null);



  // Utility functions (declare these first to avoid hoisting issues)
  const updateRealTimeAnalytics = useCallback(() => {
    if (!isLive || !livestream?.actual_start_time) {
      return;
    }

    // Update analytics duration for smooth timer
    const durationInSeconds = Math.floor((Date.now() - new Date(livestream.actual_start_time).getTime()) / 1000);

    // Use backend analytics if available, otherwise fall back to local calculation (or 0)
    const backendAnalytics = livestream.analytics || {};

    // For interactions, we can optionally add local new messages to the backend count for instant feedback
    // But for now, let's rely on the backend + polling for correctness as requested

    const newAnalytics = {
      duration: durationInSeconds, // Always calculated locally for smooth timer
      interactions: backendAnalytics.interactions || 0,
      revenue: backendAnalytics.revenue || 0,
      engagement: backendAnalytics.engagement || 0,
      total_bids: backendAnalytics.total_bids || 0,
      total_viewers: backendAnalytics.total_viewers || 0
    };

    // Only log in development for debugging
    if (process.env.NODE_ENV === 'development') {
      // console.log('📊 Analytics Update:', newAnalytics);
    }

    setAnalytics(newAnalytics);
  }, [isLive, livestream?.actual_start_time, livestream?.analytics]);

  const startAnalyticsTracking = useCallback(() => {
    if (analyticsRef.current) clearInterval(analyticsRef.current);

    analyticsRef.current = setInterval(() => {
      updateRealTimeAnalytics();
    }, 1000); // Update every second for real-time feel
  }, [updateRealTimeAnalytics]);

  const stopAnalyticsTracking = useCallback(() => {
    if (analyticsRef.current) {
      clearInterval(analyticsRef.current);
      analyticsRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);

    heartbeatRef.current = setInterval(async () => {
      try {
        await livestreamService.sendHeartbeat(livestreamId);
      } catch (error) {
        console.error('Heartbeat failed:', error);
      }
    }, 3 * 60 * 1000); // Every 3 minutes (reduced for better safety margin)
  }, [livestreamId]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const refreshLivestreamData = useCallback(async () => {
    try {
      // Refresh livestream data to get updated viewer count and other stats
      const response = await livestreamService.getLivestream(livestreamId);

      setLivestream(prev => ({
        ...prev,
        viewer_count: response.viewer_count,
        credits_consumed: response.credits_consumed,
        analytics: response.analytics
      }));

      // Update viewer count if it has changed
      if (response.viewer_count !== undefined) {
        setViewerCount(response.viewer_count);
      }

    } catch (error) {
      console.warn('Failed to refresh livestream data:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to refresh livestream data.',
        autoClose: 3000
      });
    }
  }, [livestreamId, setAlert]);

  const startPeriodicDataRefresh = useCallback(() => {
    if (dataRefreshRef.current) clearInterval(dataRefreshRef.current);

    dataRefreshRef.current = setInterval(refreshLivestreamData, 10000); // Refresh every 10 seconds
  }, [refreshLivestreamData]);

  const stopPeriodicDataRefresh = useCallback(() => {
    if (dataRefreshRef.current) {
      clearInterval(dataRefreshRef.current);
      dataRefreshRef.current = null;
    }
  }, []);

  // Credit monitoring
  const checkCredits = useCallback(async () => {
    if (!livestreamId) return;

    try {
      const response = await livestreamService.processCreditDeduction(livestreamId);

      if (response.livestream_ended) {
        setAlert({
          type: 'danger',
          message: 'Livestream ended due to insufficient credits.',
          persistent: true,
          autoClose: 10000
        });

        // Livestream is already ended on backend, just clean up frontend
        console.log('🔴 Livestream ended by server due to insufficient credits');

        // Stop all tracking
        setIsLive(false);
        stopAnalyticsTracking();
        stopHeartbeat();
        stopPeriodicDataRefresh();

        if (creditCheckRef.current) {
          clearInterval(creditCheckRef.current);
          creditCheckRef.current = null;
        }

        // Cleanup media
        if (agoraClient) {
          await agoraClient.leave();
          setAgoraClient(null);
        }
        mediaDevices.cleanup();

        // Update status
        setLivestream(prev => ({
          ...prev,
          status: 'ended',
          end_time: new Date().toISOString()
        }));

        // Redirect after delay
        setTimeout(() => {
          navigate('/livestreams');
        }, 5000);

      } else if (response.credits_consumed !== undefined) {
        // Update credits consumed
        setLivestream(prev => ({
          ...prev,
          credits_consumed: response.credits_consumed,
          seller: {
            ...prev.seller,
            seller_profile: {
              ...prev.seller?.seller_profile,
              credits: response.remaining_credits
            }
          }
        }));
      }
    } catch (error) {
      console.warn('Credit check failed:', error);
      setAlert({
        type: 'danger',
        message: 'Credit check failed.',
        autoClose: 3000
      });
    }
  }, [livestreamId, navigate, stopAnalyticsTracking, stopHeartbeat, stopPeriodicDataRefresh, mediaDevices, agoraClient, setAlert, setIsLive]);

  const startCreditMonitoring = useCallback(() => {
    if (creditCheckRef.current) clearInterval(creditCheckRef.current);
    // Check every minute to ensure timely deduction
    creditCheckRef.current = setInterval(checkCredits, 60000);
    console.log('💰 Credit monitoring started');
  }, [checkCredits]);

  const stopCreditMonitoring = useCallback(() => {
    if (creditCheckRef.current) {
      clearInterval(creditCheckRef.current);
      creditCheckRef.current = null;
      console.log('💰 Credit monitoring stopped');
    }
  }, []);

  // Enhanced media management
  const initializeMedia = useCallback(async () => {
    try {
      console.log('🎥 Initializing media devices...');

      // Get available devices first
      await mediaDevices.getDevices();
      console.log('📱 Devices enumerated:', mediaDevices.devices);

      // Request permissions and get stream
      const stream = await mediaDevices.requestPermissions();
      console.log('✅ Media permissions granted, stream obtained');

      // Set up video element
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true; // Prevent echo

        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          console.log('📹 Video metadata loaded');
          videoRef.current.play().then(() => {
            console.log('▶️ Video playing successfully');
          }).catch(err => {
            console.error('❌ Video play failed:', err);
          });
        };
      }

      // Enable video and audio by default when starting
      setTimeout(async () => {
        try {
          await mediaDevices.toggleVideo(); // Enable video
          await mediaDevices.toggleAudio(); // Enable audio
          console.log('🎬 Video and audio enabled by default');
        } catch (err) {
          console.warn('⚠️ Could not auto-enable media:', err);
        }
      }, 1000);

      setAlert({
        type: 'success',
        message: '📹 Camera and microphone initialized! Video and audio are ready.',
        autoClose: 5000
      });

      return stream;

    } catch (error) {
      console.error('❌ Media initialization failed:', error);
      setAlert({
        type: 'warning',
        message: '⚠️ Camera/microphone access denied. Please allow permissions and refresh the page.',
        persistent: true
      });
      throw error;
    }
  }, [mediaDevices, setAlert]);

  // Enhanced Agora integration
  const initializeAgora = useCallback(async () => {
    try {
      const agoraData = await livestreamService.getAgoraToken(livestreamId);

      // Check if we have dummy credentials (development mode)
      if (agoraData.token === 'dummy_token_for_development' || agoraData.app_id === 'dummy_app_id') {
        console.log('Using dummy Agora credentials, enabling basic camera/microphone for testing');

        // Request camera and microphone permissions for testing
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });

          // Display local video for testing
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }

          setAlert({
            type: 'success',
            message: 'Camera and microphone access granted. Video streaming disabled in development mode.'
          });

          return;
        } catch (permissionError) {
          console.error('Permission denied for camera/microphone:', permissionError);
          setAlert({
            type: 'warning',
            message: 'Camera/microphone permission denied. Video streaming requires permission.'
          });
          return;
        }
      }

      const { createClient, createMicrophoneAndCameraTracks } = await import('agora-rtc-sdk-ng');

      // Use h264 codec for compatibility with backend configuration
      const client = createClient({
        mode: 'live',
        codec: 'h264'  // Changed from vp8 to h264 for compatibility
      });

      await client.setClientRole('host');

      const uid = agoraData.uid || Math.floor(Math.random() * 1000000000);
      console.log(`Joining Agora with UID: ${uid}`);

      await client.join(agoraData.app_id, agoraData.channel_name, agoraData.token, uid);

      // Create local tracks with explicit configuration
      const [microphoneTrack, cameraTrack] = await createMicrophoneAndCameraTracks(
        {
          // Audio configuration
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        {
          // Video configuration for better mobile compatibility
          encoderConfig: {
            width: 1280,
            height: 720,
            frameRate: 30,
            bitrateMin: 1000,
            bitrateMax: 3000,
          },
          facingMode: 'user', // Front camera by default
        }
      );

      // Play local video
      if (videoRef.current) {
        cameraTrack.play(videoRef.current);
        console.log('✅ Local video playing in preview');
      }

      // Publish tracks (broadcaster publishes video/audio)
      await client.publish([microphoneTrack, cameraTrack]);

      console.log('✅ Seller joined Agora as broadcaster and published video/audio');
      console.log('📺 Published tracks:', {
        video: cameraTrack.getTrackLabel(),
        audio: microphoneTrack.getTrackLabel(),
        videoEnabled: cameraTrack.enabled,
        audioEnabled: microphoneTrack.enabled,
      });

      setAgoraClient(client);

      console.log('✅ Agora initialized successfully');

    } catch (error) {
      console.error('Agora initialization error:', error);
      setAlert({
        type: 'warning',
        message: 'Professional streaming unavailable. Using basic mode.',
        autoClose: 5000
      });
    }
  }, [livestreamId, setAlert, setAgoraClient]);

  // Enhanced livestream control
  const startLivestream = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🚀 Starting livestream...');

      // Step 1: Start backend livestream first (matching old panel approach)
      setAlert({
        type: 'info',
        message: '🔄 Starting livestream on server...',
        autoClose: 2000
      });

      await livestreamService.startLivestream(livestreamId);
      console.log('✅ Backend livestream started');

      // Step 2: Update state immediately after backend start
      setIsLive(true);
      setLivestream(prev => ({
        ...prev,
        status: 'live',
        actual_start_time: new Date().toISOString()
      }));

      // Step 3: Start tracking and monitoring
      setTimeout(() => {
        updateRealTimeAnalytics(); // Immediate update after state settles
        startAnalyticsTracking();
      }, 100);
      startHeartbeat();
      startPeriodicDataRefresh();
      startCreditMonitoring();

      // Step 4: Initialize media devices (optional, can fail gracefully)
      setAlert({
        type: 'info',
        message: '📹 Initializing camera and microphone...',
        autoClose: 3000
      });

      try {
        await initializeMedia();
        console.log('✅ Media initialized successfully');
      } catch (mediaError) {
        console.warn('⚠️ Media initialization failed, but livestream started:', mediaError);
        setAlert({
          type: 'warning',
          message: '🎥 Livestream started! Please click camera and microphone buttons to enable them.',
          autoClose: 5000
        });
      }

      // Step 5: Initialize Agora if available (optional)
      try {
        await initializeAgora();
        console.log('✅ Agora initialized');
      } catch (agoraError) {
        console.warn('⚠️ Agora initialization failed, using basic streaming:', agoraError);
      }

      setAlert({
        type: 'success',
        message: '🎥 Livestream started successfully! You are now LIVE! 🔴',
        autoClose: 8000
      });

      console.log('🎉 Livestream start completed successfully');

    } catch (error) {
      console.error('❌ Start livestream error:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to start livestream. Please check your credits.',
        persistent: true
      });
    } finally {
      setLoading(false);
    }
  }, [livestreamId, initializeMedia, initializeAgora, startAnalyticsTracking, startHeartbeat, updateRealTimeAnalytics, startPeriodicDataRefresh, startCreditMonitoring, setAlert, setLoading, setIsLive, setLivestream]);



  // Bidding utility functions (moved before handlers to fix dependency issues)
  const refreshProducts = useCallback(async () => {
    try {
      const data = await productService.getProductsForLivestream(livestreamId);
      setProducts(data.products || []);
    } catch (error) {
      console.error('Refresh products error:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to refresh products.',
        autoClose: 3000
      });
    }
  }, [livestreamId, setAlert, setProducts]);

  const endBidding = useCallback(async () => {
    try {
      if (currentBidding?.id) {
        await livestreamService.endBidding(currentBidding.id);
        webSocketService.endBidding(currentBidding.id);
      }

      setCurrentBidding(null);
      setBiddingTimer(0);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } catch (error) {
      console.error('End bidding error:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to end bidding. Please try again.',
        autoClose: 5000
      });
    }
  }, [currentBidding?.id, setAlert, setCurrentBidding, setBiddingTimer]);

  const startBiddingTimer = useCallback((duration) => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setBiddingTimer(prev => {
        if (prev <= 1) {
          endBidding();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [endBidding, setBiddingTimer]);

  // Enhanced initialization
  const initializeLivestream = useCallback(async () => {
    if (isInitializing) return;

    try {
      setIsInitializing(true);
      setLoading(true);

      // Parallel data loading
      const [livestreamData, productsData] = await Promise.all([
        livestreamService.getLivestream(livestreamId),
        productService.getProductsForLivestream(livestreamId).catch(() => ({ products: [] }))
      ]);

      setLivestream(livestreamData);
      setProducts(productsData.products || []);

      // Initialize viewer count from livestream data
      if (livestreamData.viewer_count !== undefined) {
        setViewerCount(livestreamData.viewer_count);
      }

      // Set live status
      if (livestreamData.status === 'live') {
        setIsLive(true);

        // Check for active bidding and restore state
        if (livestreamData.active_bidding) {
          console.log('🔄 Restoring active bidding session:', livestreamData.active_bidding);
          const activeBid = livestreamData.active_bidding;
          const now = new Date();
          const endsAt = new Date(activeBid.ends_at);
          const remainingSeconds = Math.max(0, Math.floor((endsAt - now) / 1000));

          // Only restore if time remains
          if (remainingSeconds > 0) {
            // Find product (or fallback to embedded product data)
            let product = productsData.products?.find(p => String(p.id) === String(activeBid.product)) || activeBid.product_data;

            setCurrentBidding({
              id: activeBid.id,
              product,
              startingPrice: activeBid.starting_price,
              duration: activeBid.timer_duration,
              startedAt: new Date(activeBid.started_at)
            });

            setBiddingTimer(remainingSeconds);
            setBids(activeBid.bids || []);

            // Start the timer with remaining seconds
            startBiddingTimer(remainingSeconds);

            console.log(`⏱️ Bidding timer restored with ${remainingSeconds}s remaining`);
          } else {
            console.log('⚠️ Active bidding returned but time expired');
            // Optionally auto-end it if expired
          }
        }

        // Initialize analytics immediately if stream is already live
        setTimeout(() => {
          updateRealTimeAnalytics();
          startAnalyticsTracking();
        }, 500);

        startHeartbeat();
        startPeriodicDataRefresh();
        startCreditMonitoring();

        // Simulate some initial data if in development mode
        if (process.env.NODE_ENV === 'development' && !livestreamData.active_bidding) {
          // Add some mock viewers for testing
          setTimeout(() => {
            setViewerCount(3);
          }, 2000);

          // ... (keep existing dev mode mocks, but conditioned on !active_bidding to avoid conflict)

          // Add some mock chat messages for testing
          setTimeout(() => {
            setChatMessages([
              {
                id: 1,
                content: 'The Livestream is started!',
                user_name: 'Admin',
                timestamp: new Date().toISOString()
              }
              // {
              //   id: 2,
              //   content: 'This product looks great!',
              //   user_name: 'TestUser2',
              //   timestamp: new Date().toISOString()
              // },
              // {
              //   id: 3,
              //   content: 'When will bidding start?',
              //   user_name: 'TestUser3',
              //   timestamp: new Date().toISOString()
              // }
            ]);
          }, 3000);

          // Add some mock bids for testing
          setTimeout(() => {
            setBids([
              {
                id: 1,
                amount: 150,
                user_name: 'BidderOne',
                timestamp: new Date().toISOString()
              },
              {
                id: 2,
                amount: 200,
                user_name: 'BidderTwo',
                timestamp: new Date().toISOString()
              }
            ]);
          }, 4000);
        }
      }

      // Auto-start if requested
      const shouldAutoStart = searchParams.get('autoStart') === 'true';
      if (shouldAutoStart && livestreamData.status === 'scheduled') {
        setTimeout(() => startLivestream(), 2000);
      }

      // Connect WebSocket
      await websocket.connect();

    } catch (error) {
      console.error('Initialization error:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load livestream data. Please refresh and try again.',
        persistent: true
      });
    } finally {
      setLoading(false);
      setIsInitializing(false);
    }
  }, [livestreamId, isInitializing, searchParams, websocket, setAlert, startBiddingTimer, updateRealTimeAnalytics, startAnalyticsTracking, startHeartbeat, startPeriodicDataRefresh, startCreditMonitoring, startLivestream]);

  // Auto-end livestream function
  const autoEndLivestream = useCallback(async (reason = 'Navigation') => {
    if (!isLive || !livestreamId) return;

    try {
      console.log(`🔴 Auto-ending livestream due to: ${reason}`);

      // IMMEDIATE CAMERA CLEANUP - Do this first to turn off camera light immediately
      try {
        const { forceReleaseCameraHardware, stopAllActiveMediaTracks } = await import('../../utils/helpers');
        stopAllActiveMediaTracks();
        await forceReleaseCameraHardware();
        console.log('✅ Camera hardware released immediately');
      } catch (error) {
        console.error('Error with immediate camera cleanup:', error);
      }

      // Quick cleanup to prevent multiple calls
      setIsLive(false);

      // Stop all tracking immediately
      stopAnalyticsTracking();
      stopHeartbeat();
      stopPeriodicDataRefresh();
      stopCreditMonitoring();

      // Cleanup media devices
      mediaDevices.cleanup();

      // Cleanup Agora
      if (agoraClient) {
        await agoraClient.leave();
        setAgoraClient(null);
      }

      // End backend livestream with auto_ended flag
      await livestreamService.endLivestream(livestreamId, { auto_ended: true });

      // Update state
      setLivestream(prev => ({ ...prev, status: 'ended', end_time: new Date().toISOString() }));

      console.log(`✅ Livestream auto-ended successfully due to: ${reason}`);

    } catch (error) {
      console.error(`❌ Failed to auto-end livestream (${reason}):`, error);
      setAlert({
        type: 'danger',
        message: `Failed to auto-end livestream (${reason}).`,
        autoClose: 3000
      });

      // Fallback: try with beacon API
      try {
        const token = localStorage.getItem('authToken');
        const beaconUrl = `${process.env.REACT_APP_API_URL || 'https://api.whatnew.live'}/api/livestreams/livestreams/${livestreamId}/end/`;

        if (navigator.sendBeacon && token) {
          // Create a FormData for beacon
          const formData = new FormData();
          formData.append('_method', 'PATCH');
          formData.append('auto_ended', 'true'); // Add auto_ended flag for beacon

          navigator.sendBeacon(beaconUrl, formData);
          console.log('📡 Sent beacon request to end livestream');
        }
      } catch (beaconError) {
        console.error('Beacon fallback failed:', beaconError);
      }
    }
  }, [isLive, livestreamId, stopAnalyticsTracking, stopHeartbeat, stopPeriodicDataRefresh, stopCreditMonitoring, mediaDevices, agoraClient, setAlert, setIsLive, setAgoraClient, setLivestream]);

  // Enhanced chat handler (moved before setupEventListeners)
  const handleChatMessage = useCallback((data) => {
    const messageData = data.data || data;

    setChatMessages(prev => {
      // Prevent duplicates
      if (prev.some(msg => msg.id === messageData.id)) {
        return prev;
      }

      // Add timestamp and normalize fields
      const messageWithTimestamp = {
        ...messageData,
        user_name: messageData.user_name || messageData.username || 'Anonymous',
        is_seller: messageData.is_seller || messageData.user_type === 'seller',
        timestamp: messageData.timestamp || new Date().toISOString()
      };

      // Limit chat history to 100 messages for performance
      const newMessages = [...prev, messageWithTimestamp];
      return newMessages.length > 100 ? newMessages.slice(-100) : newMessages;
    });

    // Trigger immediate analytics update for real-time engagement
    setTimeout(updateRealTimeAnalytics, 100);
  }, [updateRealTimeAnalytics, setChatMessages]);

  // Enhanced bidding handlers (moved before setupEventListeners)
  const handleBidPlaced = useCallback((data) => {
    const bidData = data.data || data;
    const newBid = {
      id: bidData.id || Date.now(),
      amount: parseFloat(bidData.amount) || 0,
      user_name: bidData.user_name || bidData.username || 'Anonymous',
      user_id: bidData.user_id,
      timestamp: bidData.timestamp || new Date().toISOString()
    };

    setBids(prev => {
      const updated = [...prev, newBid].sort((a, b) => b.amount - a.amount);
      return updated.slice(0, 10); // Keep only top 10 bids
    });

    setAlert({
      type: 'info',
      message: `💰 New bid: ₹${newBid.amount} by ${newBid.user_name}`,
      autoClose: 3000
    });

    // Trigger immediate analytics update for real-time revenue
    setTimeout(updateRealTimeAnalytics, 100);
  }, [updateRealTimeAnalytics, setAlert, setBids]);

  const handleBiddingStarted = useCallback((data) => {
    const biddingData = data.data || data;

    // Try to find product in local list first (handling type mismatch with loose equality)
    let product = products.find(p => String(p.id) === String(biddingData.product_id));

    // Fallback to embedded product data if available (sent from backend)
    if (!product && biddingData.product_data) {
      console.log('📦 Using embedded product data for bidding');
      product = biddingData.product_data;
    }

    if (!product) {
      console.warn('⚠️ Product not found for bidding:', biddingData.product_id);
      setAlert({
        type: 'warning',
        message: 'Product not found for bidding.',
        autoClose: 3000
      });
    }

    setCurrentBidding({
      id: biddingData.id,
      product,
      startingPrice: biddingData.starting_price,
      duration: parseInt(biddingData.duration || biddingData.timer_duration || 60),
      startedAt: new Date()
    });

    const duration = parseInt(biddingData.duration || biddingData.timer_duration || 60);
    setBiddingTimer(duration);
    setBids([]);

    // Start countdown
    startBiddingTimer(duration);
  }, [products, startBiddingTimer, setAlert, setCurrentBidding, setBiddingTimer, setBids]);

  const handleBiddingEnded = useCallback((data) => {
    const endData = data.data || data;

    setCurrentBidding(null);
    setBiddingTimer(0);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setAlert({
      type: 'success',
      message: endData.winner_name
        ? `🏆 Bidding won by ${endData.winner_name} for ₹${endData.winning_amount}!`
        : '⏰ Bidding ended with no winner',
      autoClose: 8000
    });

    // Refresh products to update quantities and livestream stats
    refreshProducts();
    refreshLivestreamData(); // Refresh analytics immediately
  }, [refreshProducts, refreshLivestreamData, setAlert, setCurrentBidding, setBiddingTimer]);

  // Page visibility and navigation handlers (declared before setupEventListeners)
  const handleVisibilityChange = useCallback(() => {
    const isVisible = !document.hidden;

    if (isVisible) {
      console.log('Page visible - resuming operations');
      if (isLive && !heartbeatRef.current) {
        startHeartbeat();
        startCreditMonitoring();
      }
    } else {
      console.log('Page hidden - continuing heartbeat to prevent auto-end');
      // Don't stop heartbeat when page is hidden to prevent livestream auto-ending
      // The heartbeat should continue running even when tab is backgrounded
    }
  }, [isLive, startHeartbeat, startCreditMonitoring]);

  const handleBeforeUnload = useCallback(async (event) => {
    // IMMEDIATE CAMERA CLEANUP - This is critical for navigation scenarios
    console.log('🚪 Page navigation detected - running emergency camera cleanup');

    // Use the emergency cleanup from our utility
    emergencyCameraCleanup();

    // Auto-end livestream if currently live
    if (isLive && livestreamId) {
      try {
        // Use navigator.sendBeacon for reliable cleanup during page unload
        const token = localStorage.getItem('authToken');

        // Try to end livestream using beacon (works even during page unload)
        const beaconUrl = `${process.env.REACT_APP_API_URL || 'https://api.whatnew.live'}/api/livestreams/livestreams/${livestreamId}/end/`;

        if (navigator.sendBeacon && token) {
          // Create FormData for beacon with auth token
          const formData = new FormData();
          formData.append('_method', 'PATCH');
          formData.append('_token', token);
          formData.append('auto_ended', 'true');

          navigator.sendBeacon(beaconUrl, formData);
        }

        console.log('🔴 Auto-ending livestream due to page navigation/close');
      } catch (error) {
        console.error('Failed to auto-end livestream:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to auto-end livestream on unload.',
          autoClose: 3000
        });
      }
    }

    // Cleanup resources
    mediaDevices.cleanup();
    websocket.disconnect();
    stopHeartbeat();
    stopAnalyticsTracking();
    stopCreditMonitoring();

    // For beforeunload, show confirmation dialog if livestream is active
    if (isLive) {
      event.preventDefault();
      event.returnValue = 'You are currently livestreaming. Leaving will end your stream. Are you sure?';
      return event.returnValue;
    }
  }, [isLive, livestreamId, mediaDevices, websocket, stopHeartbeat, stopAnalyticsTracking, stopCreditMonitoring, setAlert]);

  // Enhanced event listeners
  const setupEventListeners = useCallback(() => {
    // Define WebSocket event handlers
    const viewerJoinedHandler = () => {
      setViewerCount(prev => prev + 1);
      
      // Trigger analytics update for engagement calculation
      setTimeout(updateRealTimeAnalytics, 100);
    };

    const viewerLeftHandler = () => {
      setViewerCount(prev => Math.max(0, prev - 1));

      // Trigger analytics update for engagement calculation
      setTimeout(updateRealTimeAnalytics, 100);
    };

    // WebSocket events
    webSocketService.on('viewer_joined', viewerJoinedHandler);
    webSocketService.on('viewer_left', viewerLeftHandler);
    webSocketService.on('chat_message', handleChatMessage);
    webSocketService.on('bid_placed', handleBidPlaced);
    webSocketService.on('bidding_started', handleBiddingStarted);
    webSocketService.on('bidding_ended', handleBiddingEnded);

    // Page visibility and navigation events
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // IMMEDIATE camera cleanup for critical navigation events
    const immediateCleanup = () => {
      // Use the comprehensive emergency cleanup utility
      console.log('🔴 IMMEDIATE: Starting emergency camera cleanup via utility');
      emergencyCameraCleanup();

      // Additional cleanup for any remaining streams from mediaDevices
      if (mediaDevices.stream) {
        mediaDevices.stream.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
            console.log(`🔴 IMMEDIATE: Stopped active ${track.kind} track: ${track.label}`);
          }
        });
      }

      console.log('✅ IMMEDIATE: Emergency camera cleanup completed');
    };

    window.addEventListener('pagehide', () => {
      immediateCleanup();
      autoEndLivestream('Page Hide');
    });
    window.addEventListener('unload', () => {
      immediateCleanup();
      autoEndLivestream('Page Unload');
    });

    // History API interception for SPA navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      if (isLive) {
        immediateCleanup();
        autoEndLivestream('History Push State');
      }
      return originalPushState.apply(window.history, args);
    };

    window.history.replaceState = function (...args) {
      if (isLive) {
        immediateCleanup();
        autoEndLivestream('History Replace State');
      }
      return originalReplaceState.apply(window.history, args);
    };

    // Handle popstate (back/forward buttons)
    const handlePopState = () => {
      if (isLive) {
        immediateCleanup();
        autoEndLivestream('Browser Navigation (Back/Forward)');
      }
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      // Remove WebSocket event listeners
      if (webSocketService.off) {
        webSocketService.off('viewer_joined', viewerJoinedHandler);
        webSocketService.off('viewer_left', viewerLeftHandler);
        webSocketService.off('chat_message', handleChatMessage);
        webSocketService.off('bid_placed', handleBidPlaced);
        webSocketService.off('bidding_started', handleBiddingStarted);
        webSocketService.off('bidding_ended', handleBiddingEnded);
      } else if (webSocketService.removeListener) {
        webSocketService.removeListener('viewer_joined', viewerJoinedHandler);
        webSocketService.removeListener('viewer_left', viewerLeftHandler);
        webSocketService.removeListener('chat_message', handleChatMessage);
        webSocketService.removeListener('bid_placed', handleBidPlaced);
        webSocketService.removeListener('bidding_started', handleBiddingStarted);
        webSocketService.removeListener('bidding_ended', handleBiddingEnded);
      }

      // Remove DOM event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', () => autoEndLivestream('Page Hide'));
      window.removeEventListener('unload', () => autoEndLivestream('Page Unload'));
      window.removeEventListener('popstate', handlePopState);

      // Restore original history methods
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [handleVisibilityChange, handleBeforeUnload, autoEndLivestream, isLive, handleChatMessage, handleBidPlaced, handleBiddingStarted, handleBiddingEnded, updateRealTimeAnalytics, mediaDevices, setViewerCount]);




  const endLivestream = useCallback(async () => {
    try {
      setLoading(true);

      // IMMEDIATE CAMERA CLEANUP when manually ending stream
      console.log('🔚 Manually ending livestream - running camera cleanup');
      emergencyCameraCleanup();

      // Also run comprehensive cleanup for additional safety
      setTimeout(() => {
        comprehensiveCameraCleanup();
      }, 100);

      // Stop all tracking
      stopAnalyticsTracking();
      stopHeartbeat();
      stopPeriodicDataRefresh();
      stopCreditMonitoring();

      // Cleanup media devices
      mediaDevices.cleanup();

      // Cleanup Agora
      if (agoraClient) {
        await agoraClient.leave();
        setAgoraClient(null);
      }

      // End backend livestream
      await livestreamService.endLivestream(livestreamId);

      setIsLive(false);
      setLivestream(prev => ({ ...prev, status: 'ended', end_time: new Date().toISOString() }));

      setAlert({
        type: 'success',
        message: '📺 Livestream ended successfully! Thank you for streaming! Redirecting to dashboard...',
        autoClose: 4000
      });

      // Navigate after showing success message
      setTimeout(() => {
        navigate('/livestreams', { replace: true });
      }, 4000);

    } catch (error) {
      console.error('End livestream error:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to end livestream properly. Please try again.',
        persistent: true
      });
    } finally {
      setLoading(false);
    }
  }, [agoraClient, livestreamId, mediaDevices, navigate, setAlert, setLoading, setIsLive, setLivestream, setAgoraClient, stopAnalyticsTracking, stopHeartbeat, stopPeriodicDataRefresh, stopCreditMonitoring]);

  // Enhanced bidding management
  const startBidding = useCallback(async (product, duration = 60) => {
    try {
      const biddingData = {
        product: product.id,
        starting_price: product.base_price,
        timer_duration: duration
      };

      const response = await livestreamService.startBidding(livestreamId, biddingData);

      webSocketService.startBidding({
        ...biddingData,
        bidding_id: response.id
      });

      setAlert({
        type: 'success',
        message: `🏷️ Bidding started for ${product.name}!`,
        autoClose: 5000
      });

    } catch (error) {
      setAlert({
        type: 'danger',
        message: 'Failed to start bidding. Please try again.',
        autoClose: 5000
      });
    }
  }, [livestreamId, setAlert]);



  // Sync video element with stream state
  useEffect(() => {
    const updateVideoElement = () => {
      if (!videoRef.current) return;

      const currentStream = mediaDevices.stream;

      if (currentStream && mediaDevices.isVideoEnabled) {
        // Update video element with current stream
        if (videoRef.current.srcObject !== currentStream) {
          videoRef.current.srcObject = currentStream;
          videoRef.current.muted = true; // Prevent echo

          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch(err => {
              console.error('Video play error:', err);
            });
          };
        }
      } else {
        // Clear video element if no stream or video disabled
        if (videoRef.current.srcObject) {
          videoRef.current.srcObject = null;
        }
      }
    };

    updateVideoElement();
  }, [mediaDevices.stream, mediaDevices.isVideoEnabled]);

  const sendChatMessage = useCallback(() => {
    if (!newMessage.trim()) return;

    webSocketService.sendChatMessage('chat_message', {
      content: newMessage,
      message_type: 'text'
    });

    setNewMessage('');
  }, [newMessage]);

  // React Router navigation blocking
  useEffect(() => {
    let cleanup = null;

    // Block navigation if livestream is active
    if (isLive) {


      // For React Router v6, we'll use a combination of beforeunload and navigation interception


      // Intercept clicks on navigation elements (only actual navigation links, not functional buttons)
      const handleLinkClick = async (e) => {
        const target = e.target.closest('a[href]');

        // Only trigger for actual navigation links, not functional buttons or same-page actions
        if (target && isLive && target.href && !target.href.includes('#')) {
          const shouldLeave = window.confirm(
            'You are currently livestreaming. Leaving this page will end your stream. Do you want to continue?'
          );

          if (!shouldLeave) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          } else {
            // IMMEDIATE CAMERA CLEANUP before navigation
            try {
              const { forceReleaseCameraHardware, stopAllActiveMediaTracks } = await import('../../utils/helpers');
              stopAllActiveMediaTracks();
              await forceReleaseCameraHardware();
              console.log('✅ Camera released immediately on confirmed navigation');
            } catch (error) {
              console.error('Error with immediate camera cleanup:', error);
            }

            // Allow navigation but end livestream
            autoEndLivestream('User Confirmed Link Navigation');
          }
        }
      };

      // Add event listeners
      document.addEventListener('click', handleLinkClick, true);

      // Cleanup function
      cleanup = () => {
        document.removeEventListener('click', handleLinkClick, true);
      };
    }

    // Always return a cleanup function, even if it's empty
    return cleanup || (() => { });
  }, [isLive, autoEndLivestream]);

  // Component lifecycle
  useEffect(() => {
    initializeLivestream();

    return () => {
      isMountedRef.current = false;

      // IMMEDIATE CAMERA CLEANUP on component unmount
      try {
        import('../../utils/helpers').then(({ forceReleaseCameraHardware, stopAllActiveMediaTracks }) => {
          stopAllActiveMediaTracks();
          forceReleaseCameraHardware();
          console.log('✅ Camera hardware released immediately on component unmount');
        }).catch(console.error);
      } catch (error) {
        console.error('Error with immediate camera cleanup on unmount:', error);
      }

      // Auto-end livestream on component unmount
      if (isLive) {
        autoEndLivestream('Component Unmount').catch(console.error);
      }

      // Cleanup media devices
      mediaDevices.cleanup();

      // Disconnect websocket
      websocket.disconnect();

      // Stop intervals
      stopHeartbeat();
      stopAnalyticsTracking();
      stopPeriodicDataRefresh();
      stopCreditMonitoring();

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Cleanup Agora client safely
      if (agoraClient && typeof agoraClient.leave === 'function') {
        agoraClient.leave().catch(console.error);
      }
    };
  }, [initializeLivestream, isLive, autoEndLivestream, mediaDevices, websocket, stopHeartbeat, stopAnalyticsTracking, stopPeriodicDataRefresh, stopCreditMonitoring, agoraClient]);

  // Auto-initialize camera and Agora if livestream is already live
  useEffect(() => {
    if (livestream && livestream.status === 'live' && !mediaDevices.stream) {
      console.log('📹 Livestream is live but camera not initialized, auto-initializing...');
      setTimeout(async () => {
        try {
          // Request camera/microphone permissions immediately (like old panel)
          console.log('🎥 Requesting camera/microphone permissions for live stream...');
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });

          // Display local video immediately
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.muted = true; // Prevent echo
            videoRef.current.play().catch(e => console.log('Play error:', e));
          }

          setAlert({
            type: 'success',
            message: '✅ Camera and microphone access granted!'
          });

          // Now try to initialize Agora for professional streaming
          try {
            await initializeAgora();
          } catch (agoraError) {
            console.warn('Agora initialization failed, using basic camera/microphone:', agoraError);
            setAlert({
              type: 'info',
              message: '📹 Camera and microphone ready. Video streaming disabled in development mode.'
            });
          }

        } catch (permissionError) {
          console.error('Camera/microphone permission denied:', permissionError);
          setAlert({
            type: 'warning',
            message: '⚠️ Camera/microphone permission denied. Please allow camera and microphone access to start livestream.'
          });
        }
      }, 2000); // Give some time for component to settle
    }
  }, [livestream, mediaDevices.stream, initializeAgora, setAlert]);

  // Setup event listeners with proper cleanup
  useEffect(() => {
    let cleanupEventListeners = null;

    if (websocket.isConnected) {
      cleanupEventListeners = setupEventListeners();
    }

    return () => {
      if (cleanupEventListeners && typeof cleanupEventListeners === 'function') {
        cleanupEventListeners();
      }
    };
  }, [websocket.isConnected, setupEventListeners]);



  // Auto-start analytics when going live
  useEffect(() => {
    if (isLive && livestream?.actual_start_time) {
      console.log('🔄 Starting real-time analytics tracking...');
      updateRealTimeAnalytics(); // Immediate update
      startAnalyticsTracking();
    } else {
      console.log('⏹️ Stopping analytics tracking...');
      stopAnalyticsTracking();
      setAnalytics({
        duration: 0,
        interactions: 0,
        revenue: 0,
        engagement: 0
      });
    }
  }, [isLive, livestream?.actual_start_time, updateRealTimeAnalytics, startAnalyticsTracking, stopAnalyticsTracking, setAnalytics]);

  // Format helpers
  const formatTime = (seconds) => {
    // Handle invalid input
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }

    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };



  // Enhanced media control functions
  const toggleAllMedia = useCallback(async (enable) => {
    if (!mediaDevices.stream) {
      setAlert({
        type: 'warning',
        message: '⚠️ Please setup camera and microphone first.',
        autoClose: 3000
      });
      return;
    }

    const stream = mediaDevices.stream;
    const videoTrack = stream.getVideoTracks()[0];
    const audioTrack = stream.getAudioTracks()[0];

    // Directly set the tracks to the desired state
    if (videoTrack && videoTrack.enabled !== enable) {
      await mediaDevices.toggleVideo();
    }
    if (audioTrack && audioTrack.enabled !== enable) {
      await mediaDevices.toggleAudio();
    }

    setAlert({
      type: 'info',
      message: enable ? '🎥 Camera and microphone enabled!' : '🔇 Camera and microphone disabled!',
      autoClose: 2000
    });
  }, [mediaDevices, setAlert]);

  const enableAllMedia = useCallback(() => toggleAllMedia(true), [toggleAllMedia]);
  const disableAllMedia = useCallback(() => toggleAllMedia(false), [toggleAllMedia]);

  // Keyboard shortcuts for media controls
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Only process shortcuts when not typing in input fields
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl+Shift+V: Toggle Video
      if (event.ctrlKey && event.shiftKey && event.code === 'KeyV') {
        event.preventDefault();
        if (mediaDevices.stream) {
          mediaDevices.toggleVideo();
        }
      }

      // Ctrl+Shift+A: Toggle Audio
      if (event.ctrlKey && event.shiftKey && event.code === 'KeyA') {
        event.preventDefault();
        if (mediaDevices.stream) {
          mediaDevices.toggleAudio();
        }
      }

      // Ctrl+Shift+M: Mute All
      if (event.ctrlKey && event.shiftKey && event.code === 'KeyM') {
        event.preventDefault();
        disableAllMedia();
      }

      // Ctrl+Shift+E: Enable All
      if (event.ctrlKey && event.shiftKey && event.code === 'KeyE') {
        event.preventDefault();
        enableAllMedia();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [mediaDevices, disableAllMedia, enableAllMedia]);

  if (loading && !livestream) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!livestream) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <h4 className="text-muted">Livestream not found</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/livestreams')}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Livestreams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-slate-100 font-sans selection:bg-red-500/30 overflow-x-hidden">
      <header className="border-b border-[#1a2747] bg-[#0a0f1e]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1 bg-red-500/10 rounded-full border border-red-500/20">
              <span className="relative flex h-3 w-3">
                {isLive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>}
                <span className={`relative inline-flex rounded-full h-3 w-3 ${isLive ? 'bg-red-500' : 'bg-gray-500'}`}></span>
              </span>
              <span className={`font-bold text-sm tracking-wide ${isLive ? 'text-red-500' : 'text-gray-400'}`}>
                {isLive ? 'LIVE' : 'OFFLINE'}
              </span>
            </div>
            {isLive && (
              <div className="hidden md:flex items-center gap-2 text-sm text-[#2d3a5a]">
                <i className="bi bi-exclamation-triangle text-xs"></i>
                <span className="animate-pulse">Closing browser will end stream.</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 border-r border-[#1a2747] pr-6 mr-2">
              <div className="text-right">
                <div className="text-xs text-[#2d3a5a] font-bold uppercase tracking-wider">Bitrate</div>
                <div className="text-sm font-mono text-red-500">{isLive ? '5,840 kbps' : '0 kbps'}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#2d3a5a] font-bold uppercase tracking-wider">Session</div>
                <div className="text-sm font-mono text-white">{formatTime(analytics.duration)}</div>
              </div>
            </div>
            {!isLive ? (
              <button
                onClick={startLivestream}
                className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-green-600/20"
              >
                <i className="bi bi-broadcast text-xs"></i>
                Go Live
              </button>
            ) : (
              <button
                onClick={endLivestream}
                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-red-600/20"
              >
                <i className="bi bi-stop-circle text-xs"></i>
                End Stream
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6 grid grid-cols-12 gap-6 pb-24 lg:pb-6">
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {alert && (
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
              autoClose={alert.autoClose}
              persistent={alert.persistent}
              className="mb-0"
            />
          )}

          <div className="relative aspect-video bg-[#111a31] rounded-2xl overflow-hidden shadow-2xl group border border-[#1a2747]">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
            />

            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e]/90 via-transparent to-[#0a0f1e]/40 p-6 flex flex-col justify-between pointer-events-none">
              <div className="flex justify-between items-start pointer-events-auto">
                <div className="bg-[#0a0f1e]/60 backdrop-blur-md px-3 py-1.5 rounded-lg border border-transparent flex items-center gap-2">
                  <i className="bi bi-eye text-red-500 text-xs"></i>
                  <span className="text-sm font-bold text-white">{viewerCount} Viewers</span>
                </div>
                <div className="flex gap-2">
                  <button className="p-2 bg-[#0a0f1e]/60 backdrop-blur-md rounded-lg border border-transparent hover:bg-white/10 transition-colors">
                    <i className="bi bi-gear text-white text-xs"></i>
                  </button>
                </div>
              </div>
              <div className="space-y-2 pointer-events-auto">
                <h1 className="text-3xl font-bold text-white drop-shadow-md">{livestream.title}</h1>
                <p className="text-slate-300 max-w-2xl line-clamp-1">{livestream.description}</p>
                <div className="flex items-center gap-4 text-sm text-slate-400 pt-2">
                  <span className="flex items-center gap-1.5"><i className="bi bi-clock text-xs text-red-500"></i> {isLive ? formatTime(analytics.duration) : '--:--:--'}</span>
                  <span className="flex items-center gap-1.5"><i className="bi bi-tag text-xs text-red-500"></i> {livestream.category_name || (typeof livestream.category === 'object' ? livestream.category?.name : livestream.category) || 'General'}</span>
                </div>
              </div>
            </div>

            {!isLive && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <button
                  onClick={startLivestream}
                  className="p-6 rounded-full bg-red-500/20 backdrop-blur-md border border-red-500/30 shadow-[0_0_30px_rgba(255,0,51,0.4)] group transition-all hover:scale-105"
                >
                  <i className="bi bi-broadcast text-red-500 text-xs group-hover:scale-110 transition-transform"></i>
                </button>
              </div>
            )}

            {/* Camera/Mic Controls Overlay (Visible when live) */}
            {isLive && (
              <div className="absolute bottom-6 right-6 flex gap-3 z-10 transition-opacity opacity-100 lg:opacity-0 group-hover:opacity-100 pointer-events-auto">
                <button
                  onClick={mediaDevices.toggleVideo}
                  className={`p-3 rounded-xl backdrop-blur-md border border-transparent transition-all ${mediaDevices.isVideoEnabled ? 'bg-[#0a0f1e]/60 text-white shadow-lg' : 'bg-red-500 text-white shadow-lg shadow-red-500/30'}`}
                >
                  <i className={`bi ${mediaDevices.isVideoEnabled ? 'bi-camera-video' : 'bi-camera-video-off'} text-xs`}></i>
                </button>
                <button
                  onClick={mediaDevices.toggleAudio}
                  className={`p-3 rounded-xl backdrop-blur-md border border-transparent transition-all ${mediaDevices.isAudioEnabled ? 'bg-[#0a0f1e]/60 text-white shadow-lg' : 'bg-red-500 text-white shadow-lg shadow-red-500/30'}`}
                >
                  <i className={`bi ${mediaDevices.isAudioEnabled ? 'bi-mic' : 'bi-mic-mute'} text-xs`}></i>
                </button>
              </div>
            )}
          </div>

          {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button className="p-4 bg-[#111a31] rounded-xl border border-[#1a2747] flex flex-col items-center gap-2 hover:bg-red-500/10 hover:border-red-500 transition-all group shadow-sm bg-gradient-to-b from-[#111a31] to-[#0a0f1e]">
              <i className="bi bi-megaphone text-red-500 text-xl group-hover:scale-110 transition-transform"></i>
              <span className="text-xs font-bold text-white uppercase tracking-wider">Push Poll</span>
            </button>
            <button
              onClick={() => {
                if (!selectedProduct && products.length > 0) {
                  setSelectedProduct(products[0]);
                }
                setShowBiddingModal(true);
              }}
              className="p-4 bg-[#111a31] rounded-xl border border-[#1a2747] flex flex-col items-center gap-2 hover:bg-red-500/10 hover:border-red-500 transition-all group shadow-sm bg-gradient-to-b from-[#111a31] to-[#0a0f1e]"
            >
              <i className="bi bi-tag text-red-500 text-xl group-hover:scale-110 transition-transform"></i>
              <span className="text-xs font-bold text-white uppercase tracking-wider">Highlight Product</span>
            </button>
            <button className="p-4 bg-[#111a31] rounded-xl border border-[#1a2747] flex flex-col items-center gap-2 hover:bg-[#1a2747] transition-all shadow-sm bg-gradient-to-b from-[#111a31] to-[#0a0f1e]">
              <i className="bi bi-scissors text-red-500 text-xl"></i>
              <span className="text-xs font-bold text-white uppercase tracking-wider">Clip Moment</span>
            </button>
            <button className="p-4 bg-[#111a31] rounded-xl border border-[#1a2747] flex flex-col items-center gap-2 hover:bg-[#1a2747] transition-all shadow-sm bg-gradient-to-b from-[#111a31] to-[#0a0f1e]">
              <i className="bi bi-share text-red-500 text-xl"></i>
              <span className="text-xs font-bold text-white uppercase tracking-wider">Share Stream</span>
            </button>
          </div> */}

          {/* Active Bidding Display */}
          {currentBidding && (
            <div className="bg-gradient-to-r from-red-600/20 to-red-600/5 border border-red-500/30 rounded-2xl p-6 shadow-[0_0_20px_rgba(255,0,0,0.1)] relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <i className="bi bi-lightning-fill text-8xl text-red-500"></i>
              </div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 relative z-10">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse"></span>
                    <h3 className="text-red-500 font-bold uppercase tracking-widest text-xs">Active Auction</h3>
                  </div>
                  <h2 className="text-2xl font-bold text-white">{currentBidding.product?.name}</h2>
                  <p className="text-slate-400 text-xs mt-1">Highest Bid: <span className="text-white font-bold">₹{bids.length > 0 ? Math.max(...bids.map(b => b.amount)) : currentBidding.startingPrice}</span></p>
                </div>
                <div className="text-left md:text-right">
                  <div className="text-xs text-slate-400 font-bold mb-1 uppercase tracking-widest">Time Remaining</div>
                  <div className="text-4xl font-mono text-red-500 font-bold tracking-tighter drop-shadow-[0_0_10px_rgba(255,0,0,0.3)]">{formatTime(biddingTimer)}</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-6 relative z-10">
                <div className="flex-grow">
                  <div className="h-1.5 w-full bg-[#1a2747] rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-1000 ease-linear ${biddingTimer <= 10 ? 'bg-red-500 shadow-[0_0_10px_rgba(255,0,0,0.5)]' : 'bg-red-600'}`}
                      style={{ width: `${(biddingTimer / (currentBidding.duration || 60)) * 100}%` }}
                    />
                  </div>
                </div>
                <button
                  onClick={endBidding}
                  className="bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white border border-red-500/50 px-6 py-2 rounded-xl font-bold text-xs transition-all active:scale-95 shrink-0"
                >
                  End Bidding Early
                </button>
              </div>
            </div>
          )}

          <div className="bg-[#111a31] rounded-2xl border border-[#1a2747] overflow-hidden shadow-xl bg-gradient-to-b from-[#111a31] to-[#0a0f1e]">
            <div className="px-5 py-4 border-b border-[#1a2747] flex items-center justify-between">
              <h2 className="font-bold flex items-center gap-2 text-white text-sm">
                <i className="bi bi-bag text-red-500 text-lg"></i>
                Product Inventory
              </h2>
              <button
                onClick={refreshProducts}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5 transition-all"
              >
                <i className="bi bi-arrow-clockwise text-xs"></i>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#1a2747] bg-[#0a0f1e]/30">
                    <th className="px-5 py-3 text-xs font-bold text-[#2d3a5a] uppercase tracking-wider">Product</th>
                    <th className="px-5 py-3 text-xs font-bold text-[#2d3a5a] uppercase tracking-wider text-right">Price</th>
                    <th className="px-5 py-3 text-xs font-bold text-[#2d3a5a] uppercase tracking-wider text-center">Stock</th>
                    <th className="px-5 py-3 text-xs font-bold text-[#2d3a5a] uppercase tracking-wider text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1a2747]">
                  {products.map(product => (
                    <tr key={product.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden border border-[#1a2747] shrink-0">
                            <img
                              src={ImageUtils.getProductImageUrl(product)}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = ImageUtils.getPlaceholderImage(); }}
                            />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-white group-hover:text-red-500 transition-colors line-clamp-1">{product.name}</div>
                            <div className="text-xs text-slate-500 font-medium">SKU: {product.id.toString().slice(0, 8).toUpperCase()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="text-sm font-bold text-white">₹{product.base_price}</div>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${(product.available_quantity ?? product.quantity) > 0 ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`}>
                          {product.available_quantity ?? product.quantity}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowBiddingModal(true);
                          }}
                          disabled={!isLive || currentBidding || (product.available_quantity ?? product.quantity) === 0}
                          className="text-red-500 hover:text-white hover:bg-red-500 border border-red-500/30 px-3 py-1 rounded-lg text-xs font-bold transition-all disabled:opacity-20 disabled:pointer-events-none"
                        >
                          LAUNCH
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {products.length === 0 && (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-[#0a0f1e] rounded-2xl flex items-center justify-center border border-[#1a2747] mx-auto mb-4">
                  <i className="bi bi-box-seam text-slate-700 text-2xl"></i>
                </div>
                <h3 className="text-sm font-bold text-white mb-1">No Products Found</h3>
                <p className="text-xs text-slate-500">Add products to this livestream pool to see them here.</p>
              </div>
            )}
          </div>
        </div>


        <div className="col-span-12 lg:col-span-4 space-y-6">
          {/* Analytics Panel */}
          <div className="bg-[#111a31] rounded-2xl border border-[#1a2747] overflow-hidden shadow-xl bg-gradient-to-b from-[#111a31] to-[#0a0f1e]">
            <div className="px-5 py-4 border-b border-[#1a2747] flex items-center justify-between">
              <h2 className="font-bold flex items-center gap-2 text-white text-sm">
                <i className="bi bi-graph-up text-red-500 text-lg"></i>
                Live Analytics
              </h2>
              <span className="text-xs font-bold uppercase text-red-500 bg-red-500/10 px-2 py-0.5 rounded-sm border border-red-500/20">Real-time</span>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#0a0f1e]/50 rounded-xl border border-[#1a2747] hover:border-red-500/30 transition-colors">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Revenue</div>
                  <div className="text-xl font-bold text-white">₹{analytics.revenue.toLocaleString()}</div>
                  <div className="flex items-center gap-1 mt-1 text-xs text-green-500 font-bold">
                    <i className="bi bi-arrow-up"></i>
                    +12% vs last
                  </div>
                </div>
                <div className="p-4 bg-[#0a0f1e]/50 rounded-xl border border-[#1a2747] hover:border-red-500/30 transition-colors">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Engagement</div>
                  <div className="text-xl font-bold text-white">{Math.round(analytics.engagement * 100)}%</div>
                  <div className="flex items-center gap-1 mt-1 text-xs text-red-500 font-bold">
                    <i className="bi bi-activity"></i>
                    High activity
                  </div>
                </div>
                <div className="p-4 bg-[#0a0f1e]/50 rounded-xl border border-[#1a2747]">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Interactions</div>
                  <div className="text-xl font-bold text-white">{analytics.interactions.toLocaleString()}</div>
                </div>
                <div className="p-4 bg-[#0a0f1e]/50 rounded-xl border border-[#1a2747]">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Total Bids</div>
                  <div className="text-xl font-bold text-white">{analytics.total_bids || 0}</div>
                </div>
                <div className="p-4 bg-[#0a0f1e]/50 rounded-xl border border-[#1a2747]">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Credits Used</div>
                  <div className="text-xl font-bold text-white">{livestream.credits_consumed || 0}</div>
                </div>
                <div className="p-4 bg-[#0a0f1e]/50 rounded-xl border border-[#1a2747]">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">Credits Left</div>
                  <div className={`text-xl font-bold ${livestream.seller?.seller_profile?.credits < 10 ? 'text-red-500 animate-pulse' : 'text-white'}`}>
                    {livestream.seller?.seller_profile?.credits !== undefined ? livestream.seller.seller_profile.credits : '--'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Chat Panel */}
          <div className="bg-[#111a31] rounded-2xl border border-[#1a2747] flex flex-col h-[500px] shadow-xl overflow-hidden bg-gradient-to-b from-[#111a31] to-[#0a0f1e]">
            <div className="px-5 py-4 border-b border-[#1a2747] flex items-center justify-between shrink-0 bg-[#111a31]">
              <h2 className="font-bold flex items-center gap-2 text-white text-sm">
                <i className="bi bi-chat-dots text-red-500 text-lg"></i>
                Live Chat
              </h2>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${websocket.isConnected ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-red-500 animate-pulse'}`}></span>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{websocket.connectionStatus}</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-[#111a31]/50">
              {chatMessages.map((msg, index) => (
                <div key={msg.id || index} className="animate-slide-in-up">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-xs font-bold text-white shrink-0 border border-transparent shadow-lg">
                      {msg.user_name?.slice(0, 2).toUpperCase() || '??'}
                    </div>
                    <div className="space-y-1 min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-xs font-bold truncate ${msg.is_seller ? 'text-red-500' : 'text-slate-300'}`}>
                          {msg.user_name || 'Anonymous'}
                          {msg.is_seller && <i className="bi bi-patch-check-fill ms-1"></i>}
                        </span>
                        <span className="text-xs text-[#2d3a5a] font-mono shrink-0">
                          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div className={`text-sm px-3 py-2 rounded-2xl rounded-tl-none inline-block max-w-full break-words shadow-sm ${msg.is_seller ? 'bg-red-500/10 text-white border border-red-500/20' : 'bg-[#0a0f1e]/80 text-slate-300 border border-[#1a2747]'}`}>
                        {msg.content}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {chatMessages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-slate-600 opacity-50">
                  <i className="bi bi-chat-quote text-4xl mb-2"></i>
                  <p className="text-xs font-bold uppercase tracking-widest">No messages yet</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[#1a2747] shrink-0 bg-[#0a0f1e]">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendChatMessage();
                }}
                className="relative"
              >
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={isLive ? "Type a message..." : "Stream offline - Chat disabled"}
                  disabled={!isLive}
                  className="w-full bg-[#111a31] border border-[#1a2747] text-white text-sm rounded-xl py-3 pl-4 pr-12 focus:outline-none focus:border-red-500/50 transition-all placeholder:text-[#2d3a5a]"
                />
                <button
                  type="submit"
                  disabled={!isLive || !newMessage.trim()}
                  className="absolute right-2 top-2 w-8 h-8 rounded-lg bg-red-600 hover:bg-red-500 text-white flex items-center justify-center transition-all disabled:opacity-20 disabled:grayscale active:scale-90"
                >
                  <i className="bi bi-send-fill text-xs"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 h-8 bg-[#0a0f1e] border-t border-[#1a2747] z-[60] flex items-center px-6 justify-between text-xs font-bold text-[#2d3a5a] tracking-widest uppercase">
        <div className="flex gap-6">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Stream Health: EXCELLENT</span>
          <span className="flex items-center gap-1.5"><i className="bi bi-hdd-network"></i> Server: Mumbai-1</span>
        </div>
        <div className="flex gap-6">
          <span>Encoder: H.264 / NVENC</span>
          <span>Output: 1280x720 @ 30fps</span>
        </div>
      </footer>

      {/* Bidding Modal */}
      {showBiddingModal && selectedProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0a0f1e]/80 backdrop-blur-md p-4 animate-fade-in">
          <div className="w-full max-w-md bg-[#111a31] border border-[#1a2747] rounded-[32px] shadow-[0_0_80px_rgba(0,0,0,0.6)] overflow-hidden animate-scale-in">
            <div className="px-8 py-6 border-b border-[#1a2747] flex justify-between items-center bg-gradient-to-r from-[#1a2747] to-[#111a31]">
              <h5 className="text-white font-bold flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center border border-red-500/20">
                  <i className="bi bi-shield-lock-fill text-red-500 text-sm"></i>
                </div>
                Configure Auction
              </h5>
              <button
                onClick={() => setShowBiddingModal(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#2d3a5a] hover:text-white hover:bg-white/5 transition-all"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div className="flex gap-6 items-center p-4 bg-[#0a0f1e]/40 rounded-2xl border border-[#1a2747]">
                <div className="w-20 h-20 rounded-xl overflow-hidden border border-[#1a2747] shrink-0 bg-[#0a0f1e]">
                  <img
                    src={ImageUtils.getProductImageUrl(selectedProduct)}
                    alt={selectedProduct.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = ImageUtils.getPlaceholderImage(); }}
                  />
                </div>
                <div>
                  <h6 className="text-white font-bold text-lg leading-tight mb-1">{selectedProduct.name}</h6>
                  <div className="flex items-center gap-2">
                    <span className="text-red-500 font-bold">₹{selectedProduct.base_price}</span>
                    <span className="text-[#2d3a5a] text-xs">|</span>
                    <span className="text-slate-500 text-xs">{selectedProduct.available_quantity ?? selectedProduct.quantity} in stock</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-end">
                  <label className="text-xs font-bold text-[#2d3a5a] uppercase tracking-[0.2em]">Duration Presets</label>
                  <span className="text-red-500 text-xs font-bold">{selectedProduct.duration || 60} seconds</span>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {[30, 60, 120, 300].map(sec => (
                    <button
                      key={sec}
                      onClick={() => setSelectedProduct(prev => ({ ...prev, duration: sec }))}
                      className={`py-3 rounded-xl border font-bold text-xs transition-all ${(selectedProduct.duration || 60) === sec
                        ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-600/30'
                        : 'bg-[#1a2747] border-[#2d3a5a] text-slate-400 hover:border-red-500/50 hover:text-white'
                        }`}
                    >
                      {sec < 60 ? `${sec}s` : `${sec / 60}m`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-xs font-bold text-[#2d3a5a] uppercase tracking-[0.2em]">Custom Seconds</label>
                <input
                  type="number"
                  placeholder="Enter custom duration..."
                  value={selectedProduct.duration || ''}
                  onChange={(e) => setSelectedProduct(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                  className="w-full bg-[#0a0f1e] border border-[#1a2747] text-white py-4 px-6 rounded-2xl focus:outline-none focus:border-red-500 transition-all font-mono"
                />
              </div>
            </div>

            <div className="px-8 py-6 border-t border-[#1a2747] flex justify-end gap-3 bg-[#111a31]">
              <button
                onClick={() => setShowBiddingModal(false)}
                className="px-6 py-3 text-slate-400 hover:text-white transition-colors text-sm font-bold active:scale-95"
              >
                Discard
              </button>
              <button
                onClick={() => {
                  startBidding(selectedProduct, selectedProduct.duration || 60);
                  setShowBiddingModal(false);
                }}
                disabled={!selectedProduct || (selectedProduct.duration || 60) <= 0}
                className="bg-red-600 hover:bg-red-500 text-white px-10 py-3 rounded-2xl font-bold text-sm transition-all shadow-lg shadow-red-600/30 disabled:opacity-30 disabled:shadow-none flex items-center gap-2 active:scale-95 transform"
              >
                <i className="bi bi-lightning-charge-fill text-base"></i>
                Launch Auction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedLivestreamControlPanel;




